import { Trans, useTranslation } from 'next-i18next'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import styled from 'styled-components'
import { DepositAnswer } from '@fe/common/api/models/DepositAnswer'
import { Spinner } from '@fe/common/components/Spinner'
import { DropdownOptions } from '@fe/common/components/inputs/CustomSelect'
import { Dropdown } from '@fe/common/components/inputs/Dropdown'
import { ErrorText } from '@fe/common/components/typography/ErrorText'
import { FONT_WEIGHT } from '@fe/common/constants/main'
import { formatDataToOptions } from '@fe/common/utils/arrayUtils'
import { ReceiveData } from '..'
import { requisitesErrorText } from './requisitesErrorText'
import { BanksApi } from 'src/api/BanksApi'
import { DepositsApi } from 'src/api/DepositsApi'

const StyledDropdown = styled(Dropdown)`
  margin-bottom: 4rem;
`

const BankLink = styled.a`
  font-weight: ${FONT_WEIGHT.SEMIBOLD};
  text-decoration: underline;

  :hover {
    opacity: 0.7;
  }
`

export const InterbankDetails: React.FC = () => {
  const { t } = useTranslation('transactions')
  const [banks, setBanks] = useState<DropdownOptions>([])
  const [details, setDetails] = useState<DepositAnswer>()
  const [areBanksLoading, setAreBanksLoading] = useState(false)
  const [areDetailsLoading, setAreDetailsLoading] = useState(false)
  const [detailsError, setDetailsError] = useState<string>()
  // The bank can change while its instruction loads. Only the latest fetch
  // may set state, so a late answer of the previous bank does not land.
  const lastDetailsRequest = useRef(0)

  const { control, watch, setValue } = useFormContext<ReceiveData>()

  const settings_id = watch('settings_id')
  const interbank_id = watch('requestTypeFields.interbank_id') as number

  const fetchBanks = useCallback(async (settingsId: number) => {
    try {
      setAreBanksLoading(true)

      const { data } = await BanksApi.getBanks(settingsId)

      const bankOptions = formatDataToOptions(data)

      setBanks(bankOptions)
    } catch {
    } finally {
      setAreBanksLoading(false)
    }
  }, [])

  const fetchDetials = useCallback(
    async (settingsId: number, interbankId: number) => {
      const request = ++lastDetailsRequest.current
      const isLatest = () => request === lastDetailsRequest.current

      setDetails(null)
      setDetailsError(undefined)

      try {
        setAreDetailsLoading(true)

        const { data } = await DepositsApi.getInterbankDetails(
          settingsId,
          interbankId
        )

        if (isLatest()) {
          setDetails(data)
        }
      } catch (error) {
        if (isLatest()) {
          setDetailsError(requisitesErrorText(error, t))
        }
      } finally {
        if (isLatest()) {
          setAreDetailsLoading(false)
        }
      }
    },
    []
  )

  useEffect(() => {
    setValue('requestTypeFields.interbank_id', null)
    // Another currency: drop the instruction, its error and any answer still
    // on its way for the previous one.
    lastDetailsRequest.current++
    setDetails(null)
    setDetailsError(undefined)
    setAreDetailsLoading(false)

    fetchBanks(settings_id)
  }, [settings_id])

  useEffect(() => {
    if (!interbank_id) {
      return
    }

    fetchDetials(settings_id, interbank_id)
  }, [interbank_id])

  return (
    <>
      <StyledDropdown
        name="requestTypeFields.interbank_id"
        label={t('bank-name')}
        options={banks}
        control={control}
        isLoading={areBanksLoading}
        isFullWidth
      />

      {areDetailsLoading ? (
        <Spinner />
      ) : detailsError ? (
        <ErrorText>{detailsError}</ErrorText>
      ) : details ? (
        details.description ? (
          <Trans
            defaults={details.description}
            t={t}
            values={{
              uid: details.uid,
              account_number: details.payment_details,
              bank_name: details.bank?.name,
              brand: details.brand,
              local_number: details.domestic?.number,
              payment_link: '',
              registration_url: details.registration_url,
            }}
            components={{
              p: <p />,
              ol: <ol />,
              li: <li />,
              b: <b />,
              a: <BankLink rel="noopener noreferrer" target="_blank" />,
            }}
          />
        ) : (
          <Trans
            i18nKey="interbank-instruction"
            t={t}
            values={{
              bank_name: details.bank?.name,
              local_number: details.domestic?.number,
              payment_details: details.payment_details,
              registration_url: details.registration_url,
            }}
            components={{
              p: <p />,
              ol: <ol />,
              li: <li />,
              b: <b />,
              a: (
                <BankLink
                  href={details.registration_url}
                  rel="noopener noreferrer"
                  target="_blank"
                />
              ),
            }}
          />
        )
      ) : null}
    </>
  )
}
